'use client';

import React, { forwardRef, useState } from 'react';
import { Input } from '../../atoms/Input';
import { cn } from '../../utils/cn';

export interface SearchResult {
  id: string;
  title: string;
  description?: string;
}

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  onSearch?: (query: string) => void;
  onClear?: () => void;
  onChange?: (value: string) => void;
  suggestions?: SearchResult[];
  onSuggestionSelect?: (suggestion: SearchResult) => void;
  showClearButton?: boolean;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      placeholder = 'Search...',
      value: controlledValue,
      onChange,
      onSearch,
      onClear,
      suggestions = [],
      onSuggestionSelect,
      showClearButton = true,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

    const value = controlledValue !== undefined ? String(controlledValue) : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (onChange) {
        onChange(newValue);
      } else {
        setInternalValue(newValue);
      }
      setShowSuggestions(true);
      setActiveSuggestionIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
          handleSuggestionSelect(suggestions[activeSuggestionIndex]);
        } else if (onSearch) {
          onSearch(value);
          setShowSuggestions(false);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };

    const handleClear = () => {
      if (onChange) {
        onChange('');
      } else {
        setInternalValue('');
      }
      setShowSuggestions(false);
      if (onClear) {
        onClear();
      }
    };

    const handleSuggestionSelect = (suggestion: SearchResult) => {
      setShowSuggestions(false);
      if (onSuggestionSelect) {
        onSuggestionSelect(suggestion);
      }
    };

    const searchIcon = (
      <svg
        className="w-5 h-5 text-velux-grey cursor-pointer"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        onClick={() => onSearch && onSearch(value)}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    );

    const clearIcon = value && showClearButton && (
      <svg
        className="w-5 h-5 text-velux-grey cursor-pointer"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        onClick={handleClear}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    );

    return (
      <div className={cn('relative', className)}>
        <Input
          ref={ref}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onMouseEnter={() => setActiveSuggestionIndex(-1)}
          rightIcon={
            <div className="flex items-center gap-2">
              {clearIcon}
              {searchIcon}
            </div>
          }
          {...props}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div
            role="listbox"
            aria-label="Search suggestions"
            className="absolute z-50 top-full left-0 right-0 bg-velux-white shadow-md border border-velux-light-grey rounded-lg"
          >
            <ul className="list-none m-0 p-0">
              {suggestions.map((suggestion, index) => (
                <li key={suggestion.id} className="border-none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === activeSuggestionIndex}
                    className={cn(
                      'w-full text-left px-4 py-3 focus:outline-none transition-colors text-velux-black border-none bg-transparent',
                      index === activeSuggestionIndex
                        ? 'bg-velux-ui-grey'
                        : 'hover:bg-velux-ui-grey'
                    )}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    onMouseEnter={() => setActiveSuggestionIndex(index)}
                  >
                    <div className="text-sm">{suggestion.title}</div>
                    {suggestion.description && (
                      <p className="text-xs text-velux-grey mt-1">
                        {suggestion.description}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
